import { supabase } from './supabase';
import {
  DEFAULT_SAFETY_VALVE_DATA,
  computeSafetyValveMetrics,
  finiteOrNull,
  inferSafetyValveProfile,
  mergeSafetyValveData,
  pressureToBar,
  safetyValveBlock,
  validateSafetyValveRows,
} from './safety-valve-calibration-profile';

export { getTodayDailyEnvironmentLog, upsertTodayDailyEnvironmentLog } from './api-pressure-manometer-pilot';

export const DEFAULT_VALVE_VALIDITY_MONTHS = 6;
const VALIDITY_DAYS_BY_MONTHS = { 3: 90, 6: 181, 12: 365, 24: 730 };

async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

function sanitizePatch(patch = {}) {
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined && !(typeof value === 'number' && !Number.isFinite(value))));
}

export function resolveValveValidityDays(months = DEFAULT_VALVE_VALIDITY_MONTHS) {
  const value = Number(months);
  return VALIDITY_DAYS_BY_MONTHS[value] ?? Math.round((365 / 12) * (Number.isFinite(value) && value > 0 ? value : DEFAULT_VALVE_VALIDITY_MONTHS));
}

export function addValveValidityDaysToIsoDate(isoDate, months = DEFAULT_VALVE_VALIDITY_MONTHS) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + resolveValveValidityDays(months));
  return date.toISOString().slice(0, 10);
}

async function getTodayEnvironment() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try { return await getTodayDailyEnvironmentLog(); } catch { return null; }
}

export async function createSafetyValveSession({
  instrumentId,
  serviceOrderId = null,
  serviceOrderCode = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_VALVE_VALIDITY_MONTHS,
  templateCode = '',
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');
  const { data: instrument, error: instrumentError } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance, calibration_place,
             category:category_id ( name ),
             client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId)
    .single();
  if (instrumentError) throw instrumentError;

  const profile = inferSafetyValveProfile(instrument, templateCode);
  const environment = await getTodayEnvironment();
  const familyData = mergeSafetyValveData({
    ...DEFAULT_SAFETY_VALVE_DATA,
    profile_mode: profile.profileMode,
    pressure_unit: profile.unit,
    require_closing: profile.requireClosing,
    set_pressure: finiteOrNull(instrument.range_max),
    opening_tolerance_percent: finiteOrNull(instrument.tolerance),
    environment_readings: DEFAULT_SAFETY_VALVE_DATA.environment_readings.map((row) => ({
      ...row,
      temp_c: finiteOrNull(environment?.temp_c),
      pressure_hpa: finiteOrNull(environment?.pressure_hpa),
      humidity_rh: finiteOrNull(environment?.humidity_rh),
    })),
  });
  const calibrationDate = new Date().toISOString().slice(0, 10);
  const payload = {
    family: 'valve',
    instrument_id: instrument.id,
    service_order_id: serviceOrderId,
    service_order_code: serviceOrderCode || null,
    certificate_type: certificateType,
    status: 'draft',
    template_code: profile.code,
    template_revision: profile.revision,
    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null,
    client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null,
    client_province: instrument?.client?.province || null,
    instrument_object: instrument?.category?.name || instrument?.type || profile.title,
    instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null,
    instrument_unit: profile.unit,
    instrument_resolution: finiteOrNull(instrument?.resolution),
    instrument_range_min: finiteOrNull(instrument?.range_min),
    instrument_range_max: finiteOrNull(instrument?.range_max),
    instrument_class: finiteOrNull(instrument?.tolerance),
    instrument_location: instrument?.calibration_place || 'Laboratorio',
    ambient_pressure_avg: finiteOrNull(environment?.pressure_hpa),
    ambient_temp_avg: finiteOrNull(environment?.temp_c),
    ambient_humidity_avg: finiteOrNull(environment?.humidity_rh),
    calibration_date: calibrationDate,
    validity_months: Number(validityMonths) || DEFAULT_VALVE_VALIDITY_MONTHS,
    valid_until: addValveValidityDaysToIsoDate(calibrationDate, validityMonths),
    family_data: familyData,
    created_by: await currentUserId(),
  };
  const { data: session, error } = await supabase.from('calibration_sessions').insert(payload).select('*').single();
  if (error) throw error;
  const { error: blockError } = await supabase.from('calibration_session_blocks').insert({ ...safetyValveBlock(), session_id: session.id });
  if (blockError) throw blockError;
  return session;
}

export async function listSafetyValveStandardOptions({ onDate, targetPressureBar } = {}) {
  const { data, error } = await supabase.rpc('list_safety_valve_standard_options', {
    p_on_date: onDate || new Date().toISOString().slice(0, 10),
    p_target_pressure_bar: finiteOrNull(targetPressureBar),
  });
  if (error) throw error;
  return data || [];
}

export async function validateSafetyValveStandard(sessionId) {
  const { data, error } = await supabase.rpc('validate_safety_valve_session_standard', { p_session_id: sessionId });
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar la trazabilidad.'], configuration: {} };
}

export async function configureSafetyValveStandard(sessionId, capabilityId) {
  const { data, error } = await supabase.rpc('configure_safety_valve_session_standard', {
    p_session_id: sessionId,
    p_reference_capability_id: capabilityId,
  });
  if (error) throw error;
  return data;
}

export async function getSafetyValveSession(sessionId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*').eq('id', sessionId).eq('family', 'valve').single();
  if (error) throw error;
  return data;
}

export async function updateSafetyValveSession(sessionId, patch) {
  const normalized = sanitizePatch(patch);
  if (normalized.family_data) normalized.family_data = mergeSafetyValveData(normalized.family_data);
  const { data, error } = await supabase.from('calibration_sessions').update({
    ...normalized,
    updated_at: new Date().toISOString(),
    updated_by: await currentUserId(),
  }).eq('id', sessionId).eq('family', 'valve').select('*').single();
  if (error) throw error;
  return data;
}

export async function listSafetyValveSessionsByInstrument(instrumentId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*')
    .eq('instrument_id', instrumentId).eq('family', 'valve')
    .or('template_code.is.null,template_code.neq.VALVE:check_nonreturn:v1')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getSafetyValveBlock(sessionId) {
  const { data, error } = await supabase.from('calibration_session_blocks').select('*')
    .eq('session_id', sessionId).eq('block_key', 'valve_performance').single();
  if (error) throw error;
  return data;
}

export async function listSafetyValvePoints(sessionId) {
  const { data, error } = await supabase.from('calibration_session_points').select('*')
    .eq('session_id', sessionId).order('series_no').order('direction');
  if (error) throw error;
  return data || [];
}

export async function listSafetyValveResults(sessionId) {
  const { data, error } = await supabase.from('calibration_session_results').select('*')
    .eq('session_id', sessionId).order('point_no');
  if (error) throw error;
  return data || [];
}

export function safetyValvePointsToRows(points = [], session = {}) {
  const familyData = mergeSafetyValveData(session.family_data);
  return Array.from({ length: familyData.repeat_count }, (_, index) => {
    const trial = index + 1;
    const opening = points.find((point) => Number(point.series_no) === trial && point.direction === 'OPENING');
    const closing = points.find((point) => Number(point.series_no) === trial && point.direction === 'CLOSING');
    return {
      trial_no: trial,
      opening_reading: opening?.pattern_reading ?? '',
      closing_reading: closing?.pattern_reading ?? '',
    };
  });
}

export async function saveSafetyValvePoints(sessionId, block, rows = [], familyData = {}) {
  const merged = mergeSafetyValveData(familyData);
  const target = finiteOrNull(merged.set_pressure);
  const payload = [];
  rows.slice(0, merged.repeat_count).forEach((row, index) => {
    const series = index + 1;
    const opening = finiteOrNull(row.opening_reading);
    const closing = finiteOrNull(row.closing_reading);
    if (opening !== null) payload.push({
      session_id: sessionId, block_id: block.id, point_no: 1, series_no: series, direction: 'OPENING',
      setpoint: target, pattern_reading: opening, instrument_reading: target, unit: merged.pressure_unit,
    });
    if (closing !== null) payload.push({
      session_id: sessionId, block_id: block.id, point_no: 2, series_no: series, direction: 'CLOSING',
      setpoint: target, pattern_reading: closing, instrument_reading: target, unit: merged.pressure_unit,
    });
  });
  const { error: deleteError } = await supabase.from('calibration_session_points').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  if (!payload.length) return [];
  const { data, error } = await supabase.from('calibration_session_points').insert(payload).select('*');
  if (error) throw error;
  return data || [];
}

export async function calculateSafetyValveResults(sessionId) {
  const validation = await validateSafetyValveStandard(sessionId);
  if (!validation?.valid) throw new Error((validation?.issues || ['La trazabilidad no es válida.']).join(' '));
  const [session, block, points] = await Promise.all([
    getSafetyValveSession(sessionId), getSafetyValveBlock(sessionId), listSafetyValvePoints(sessionId),
  ]);
  const snapshot = validation.configuration?.reference || null;
  const familyData = mergeSafetyValveData({ ...session.family_data, standard_configuration: { reference: snapshot } });
  const rows = safetyValvePointsToRows(points, { ...session, family_data: familyData });
  const issues = validateSafetyValveRows(rows, familyData, snapshot);
  if (issues.length) throw new Error(issues.join(' '));
  const metrics = computeSafetyValveMetrics(rows, { ...session, family_data: familyData }, snapshot, block);
  if (!metrics) throw new Error('No fue posible calcular los resultados de la válvula.');
  const results = [{
    session_id: sessionId, block_id: block.id, point_no: 1,
    pattern_mean: metrics.set_pressure, instrument_mean: metrics.opening.mean,
    error_abs: metrics.error_abs, error_pct: metrics.error_pct, hysteresis: metrics.blowdown_percent,
    repeatability: metrics.opening.repeatability, u_standard: metrics.opening.u_standard,
    u_drift: 0, u_resolution: metrics.opening.u_resolution, u_repeatability: metrics.opening.u_repeatability,
    u_temp: 0, u_other: Math.hypot(metrics.opening.u_hysteresis, metrics.opening.u_other),
    uc: metrics.opening.uc, k: metrics.opening.k, u_expanded: metrics.opening.u_expanded,
    u_reported: metrics.opening.u_expanded, in_tolerance: metrics.opening_pass,
    uncertainty_components: {
      standard: metrics.opening.u_standard, resolution: metrics.opening.u_resolution,
      repeatability: metrics.opening.u_repeatability, hysteresis: metrics.opening.u_hysteresis,
      other: metrics.opening.u_other, blowdown_u_expanded_percent: metrics.blowdown_u_expanded_percent,
      decision_rule: metrics.decision_rule, evaluation_mode: metrics.evaluation_mode,
    },
  }];
  if (metrics.closing) results.push({
    session_id: sessionId, block_id: block.id, point_no: 2,
    pattern_mean: metrics.opening.mean, instrument_mean: metrics.closing.mean,
    error_abs: metrics.closing.mean - metrics.opening.mean, error_pct: metrics.blowdown_percent,
    hysteresis: null, repeatability: metrics.closing.repeatability, u_standard: metrics.closing.u_standard,
    u_drift: 0, u_resolution: metrics.closing.u_resolution, u_repeatability: metrics.closing.u_repeatability,
    u_temp: 0, u_other: Math.hypot(metrics.closing.u_hysteresis, metrics.closing.u_other),
    uc: metrics.closing.uc, k: metrics.closing.k, u_expanded: metrics.closing.u_expanded,
    u_reported: metrics.closing.u_expanded, in_tolerance: metrics.blowdown_pass,
    uncertainty_components: {
      standard: metrics.closing.u_standard, resolution: metrics.closing.u_resolution,
      repeatability: metrics.closing.u_repeatability, hysteresis: metrics.closing.u_hysteresis,
      other: metrics.closing.u_other, blowdown_u_expanded_percent: metrics.blowdown_u_expanded_percent,
      decision_rule: metrics.decision_rule, evaluation_mode: metrics.evaluation_mode,
    },
  });
  const { error: deleteError } = await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  const { error } = await supabase.from('calibration_session_results').insert(results);
  if (error) throw error;
  await updateSafetyValveSession(sessionId, {
    status: 'calculated',
    instrument_unit: familyData.pressure_unit,
    family_data: mergeSafetyValveData({ ...familyData, result_summary: metrics }),
  });
  return results;
}

export function targetPressureBar(familyData = {}) {
  const merged = mergeSafetyValveData(familyData);
  return pressureToBar(merged.set_pressure, merged.pressure_unit, merged.profile_mode);
}
